import React, { useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { useConfig } from '../../context/ConfigContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { FileText, Download, Upload, Search, Eye, Edit, Plus, FileSignature, ShieldCheck, Wrench, FileBadge, Briefcase } from 'lucide-react';
import { formLibrary } from '../../data/mock';
import { toCSV, downloadFile, printElement } from '../../lib/importExport';
import { toast } from 'sonner';

const categoryIcons = { Inspection: Eye, Compliance: ShieldCheck, Service: Wrench, Contract: FileSignature, Billing: FileText, Sales: FileBadge, HR: Briefcase };
const categoryColor = (c) => ({ Inspection:'bg-blue-100 text-blue-700', Compliance:'bg-red-100 text-red-700', Service:'bg-emerald-100 text-emerald-700', Contract:'bg-purple-100 text-purple-700', Billing:'bg-amber-100 text-amber-700', Sales:'bg-orange-100 text-orange-700', HR:'bg-slate-100 text-slate-700'}[c]);

export default function FormsPage() {
  const [q, setQ] = useState('');
  const [tab, setTab] = useState('All');
  const [preview, setPreview] = useState(null);

  const categories = ['All', ...Array.from(new Set(formLibrary.map(f=>f.category)))];
  const filtered = formLibrary.filter(f => (tab==='All'||f.category===tab) && (!q || f.name.toLowerCase().includes(q.toLowerCase()) || f.code.toLowerCase().includes(q.toLowerCase())));

  const onExport = () => {
    const csv = toCSV(formLibrary, [{key:'code',header:'Code'},{key:'name',header:'Name'},{key:'category',header:'Category'},{key:'fields',header:'Field Count'}]);
    downloadFile('xpertpests-forms-library.csv', csv); toast.success('Exported forms list');
  };
  const onDownloadPDF = (f) => { setPreview(f); setTimeout(() => printElement('#form-print-zone'), 100); };

  return (
    <div className="p-6 lg:p-8 space-y-5">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div><h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Forms Library</h1><p className="text-slate-500 mt-1">{formLibrary.length} industry-standard pest control forms.</p></div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onExport} className="gap-1.5"><Download className="w-4 h-4"/>Export List</Button>
          <Button className="bg-[#8BC53F] hover:bg-[#6FA82C] gap-1.5"><Plus className="w-4 h-4"/>Create Custom Form</Button>
        </div>
      </div>

      <Card className="border-slate-200">
        <div className="p-4 border-b border-slate-200 flex gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
            <Input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search by name or code (e.g. NPMA-33)…" className="pl-9 h-10"/>
          </div>
        </div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-transparent px-4 pt-3 h-auto flex-wrap justify-start gap-1 rounded-none border-b border-slate-100">
            {categories.map(c => <TabsTrigger key={c} value={c} className="data-[state=active]:bg-[#8BC53F] data-[state=active]:text-white">{c}</TabsTrigger>)}
          </TabsList>
          <TabsContent value={tab} className="p-5 mt-0">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map(f => {
                const Ico = categoryIcons[f.category] || FileText;
                return (
                  <Card key={f.id} className="pp-lift p-5 border-slate-200 flex flex-col">
                    <div className="flex items-start justify-between">
                      <div className="w-11 h-11 rounded-lg bg-[#8BC53F]/10 text-[#8BC53F] flex items-center justify-center"><Ico className="w-5 h-5"/></div>
                      <Badge className={`${categoryColor(f.category)} border-0`}>{f.category}</Badge>
                    </div>
                    <div className="mt-3 flex items-center gap-2"><span className="text-[10px] font-mono font-bold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">{f.code}</span><span className="text-xs text-slate-500">{f.fields} fields</span></div>
                    <div className="font-bold text-slate-900 mt-1.5 leading-snug">{f.name}</div>
                    <p className="text-xs text-slate-600 mt-2 leading-relaxed flex-1">{f.desc}</p>
                    <div className="flex gap-2 mt-4">
                      <Button size="sm" variant="outline" className="flex-1" onClick={()=>setPreview(f)}><Eye className="w-3.5 h-3.5 mr-1"/>Preview</Button>
                      <Button size="sm" className="bg-[#8BC53F] hover:bg-[#6FA82C] flex-1" onClick={()=>onDownloadPDF(f)}><Download className="w-3.5 h-3.5 mr-1"/>PDF</Button>
                    </div>
                  </Card>
                );
              })}
            </div>
            {filtered.length===0 && <div className="text-center py-10 text-slate-500">No forms match your search.</div>}
          </TabsContent>
        </Tabs>
      </Card>

      <Dialog open={!!preview} onOpenChange={(v)=>!v&&setPreview(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{preview?.name}</DialogTitle></DialogHeader>
          {preview && <FormPreview form={preview}/>}
          <div className="flex justify-end gap-2 pt-3">
            <Button variant="outline" onClick={()=>setPreview(null)}>Close</Button>
            <Button onClick={()=>printElement('#form-print-zone')} className="bg-[#8BC53F] hover:bg-[#6FA82C] gap-1.5"><Download className="w-4 h-4"/>Download PDF</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FormPreview({ form }) {
  const { contact } = useConfig();
  return (
    <div id="form-print-zone" className="bg-white p-6 rounded-lg border border-slate-200">
      <div className="flex justify-between items-start border-b-2 border-[#8BC53F] pb-4 mb-5">
        <div>
          <div className="brand text-[#8BC53F] font-extrabold text-lg">{contact.companyName.toUpperCase()}</div>
          <div className="text-xs text-slate-500">{contact.address} · {contact.phone1} · {contact.email}</div>
        </div>
        <div className="text-right">
          <div className="text-xs font-mono font-bold text-slate-500">FORM {form.code}</div>
          <div className="text-xs text-slate-500">{form.category}</div>
        </div>
      </div>
      <h2 className="text-xl font-extrabold text-slate-900">{form.name}</h2>
      <p className="text-sm text-slate-600 mt-1 mb-4">{form.desc}</p>
      <div className="grid grid-cols-2 gap-3 text-xs">
        <FormField label="Customer Name"/><FormField label="Service Address"/>
        <FormField label="Inspector / Technician"/><FormField label="License #"/>
        <FormField label="Date of Service"/><FormField label="Time On / Off Site"/>
        {Array.from({length: form.fields - 6}).map((_, i) => <FormField key={i} label={fieldLabels[i % fieldLabels.length]}/>)}
      </div>
      <div className="mt-6 pt-5 border-t border-slate-200 grid grid-cols-2 gap-6">
        <div><div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Customer Signature</div><div className="h-14 border-b border-slate-300 mt-1"/></div>
        <div><div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Technician Signature</div><div className="h-14 border-b border-slate-300 mt-1"/></div>
      </div>
      <div className="text-[10px] text-slate-400 mt-6 text-center">Generated by Xpertpests Solutions · Form ID {form.id} · {new Date().toLocaleDateString()}</div>
    </div>
  );
}

const fieldLabels = ['Target Pest','Active Ingredient','EPA Reg #','Concentration','Application Method','Area Treated (sq ft)','Quantity Used','Conducive Conditions','Findings','Recommendations','Re-Treatment Schedule','Notes','Weather Conditions','Wind Speed','Temperature','Container Lot #','Device Type','Device #','Activity Level','Bait Consumption'];

function FormField({ label }) {
  return (
    <div>
      <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{label}</div>
      <div className="h-7 border-b border-slate-300 mt-1"/>
    </div>
  );
}
